import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Container,
  Divider,
  Group,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  PinInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconMail,
  IconLock,
  IconArrowRight,
  IconShieldLock,
} from "@tabler/icons-react";
import { useAuth } from "../../hooks/useAuth";
import "./styles.css";

const Login = () => {
  const navigate = useNavigate();
  const { login, verifyTwoFactor, resendOTP, isAuthenticated, isLoading } =
    useAuth();
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"login" | "otp">("login");
  const [otp, setOtp] = useState("");
  const [email, setEmail] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    document.title = "Login | Virel Admin";
  }, []);

  useEffect(() => {
    let interval: any;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : "Invalid email",
      password: (value: string) =>
        value.length < 6 ? "Password must be at least 6 characters" : null,
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setError(null);
    const result = await login(values);
    if (result.success) {
      if (result.requiresTwoFactor) {
        setStep("otp");
        setEmail(result.email || values.email);
        setResendTimer(45);
        return;
      }
      navigate("/d");
      return;
    }
    setError(result.message || "Invalid login credentials");
  };

  const handleVerifyOtp = async () => {
    setError(null);
    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    const result = await verifyTwoFactor({ email, otp });
    if (result.success) {
      navigate("/d");
      return;
    }
    setError(result.message || "Invalid OTP");
  };
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setError(null);
    const result = await resendOTP(email);
    if (result.success) {
      setResendTimer(45);
      setOtp("");
    } else {
      setError(result.message || "Failed to resend OTP");
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/d");
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="admin-login">
      <div className="login-background">
        <div className="login-orb orb-1"></div>
        <div className="login-orb orb-2"></div>
        <div className="login-orb orb-3"></div>
        <div className="login-grid"></div>
      </div>

      <Container size="lg" className="login-container">
        <div className="login-shell">
          <div className="login-brand">
            <div className="login-brand-top">
              <div className="login-logo">
                <img src="/icon.png" alt="Virel Logo" style={{ height: 40 }} />
              </div>
              <div>
                <Text className="login-brand-kicker">Virel</Text>
                <Title order={1} className="login-brand-title">
                  Admin Dashboard
                </Title>
              </div>
            </div>

            <Text className="login-brand-subtitle">
              Secure access for operations, approvals, and platform monitoring.
            </Text>

            <div className="login-brand-points">
              <div className="login-brand-point">
                <IconShieldLock size={18} />
                <Text className="login-brand-point-text">
                  Role-protected access
                </Text>
              </div>
              <div className="login-brand-point">
                <IconArrowRight size={18} />
                <Text className="login-brand-point-text">
                  Fast navigation to dashboards
                </Text>
              </div>
              <div className="login-brand-point">
                <IconLock size={18} />
                <Text className="login-brand-point-text">
                  Session-based authentication
                </Text>
              </div>
            </div>

            <Text className="login-brand-footer">
              Use your admin credentials to continue.
            </Text>
          </div>

          <Paper className="login-card" shadow="xl">
            {step === "login" ? (
              <Stack gap="lg" className="login-content">
                <div className="login-header">
                  <Title order={2} className="login-title">
                    Sign in
                  </Title>
                  <Text size="sm" c="dimmed" className="login-subtitle">
                    Enter your email and password to access the admin panel.
                  </Text>
                </div>

                <Divider className="login-divider" />

                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Stack gap="md">
                    <TextInput
                      placeholder="you@email.com"
                      label="Email"
                      size="md"
                      leftSection={<IconMail size={18} />}
                      classNames={{
                        input: "login-input",
                        label: "login-label",
                      }}
                      {...form.getInputProps("email")}
                    />

                    <PasswordInput
                      placeholder="Enter your password"
                      label="Password"
                      size="md"
                      leftSection={<IconLock size={18} />}
                      classNames={{
                        input: "login-input",
                        label: "login-label",
                      }}
                      {...form.getInputProps("password")}
                    />

                    {error && (
                      <Alert
                        color="red"
                        variant="light"
                        className="login-error"
                      >
                        {error}
                      </Alert>
                    )}

                    <Group justify="space-between" className="login-meta" />

                    <Button
                      type="submit"
                      size="md"
                      fullWidth
                      loading={isLoading}
                      rightSection={<IconArrowRight size={18} />}
                      className="login-button"
                    >
                      Sign In
                    </Button>

                    <Text
                      size="sm"
                      c="dimmed"
                      style={{
                        textAlign: "center",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => navigate("/reset-password")}
                    >
                      Forgot password?
                    </Text>

                    <Text size="xs" c="dimmed" className="login-legal">
                      By continuing, you acknowledge that this area is
                      restricted to authorized administrators.
                    </Text>
                  </Stack>
                </form>
              </Stack>
            ) : (
              <Stack gap="lg" className="login-content">
                <div className="login-header">
                  <Title order={2} className="login-title">
                    Verification
                  </Title>
                  <Text size="sm" c="dimmed" className="login-subtitle">
                    Enter the 6-digit code sent to <b>{email}</b>
                  </Text>
                </div>

                <Divider className="login-divider" />

                <Stack gap="md" align="center">
                  <PinInput
                    size="xl"
                    length={6}
                    type="number"
                    value={otp}
                    onChange={setOtp}
                    oneTimeCode
                    autoFocus
                    className="login-pin-input"
                  />

                  {error && (
                    <Alert
                      color="red"
                      variant="light"
                      className="login-error"
                      style={{ width: "100%" }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Button
                    onClick={handleVerifyOtp}
                    size="md"
                    fullWidth
                    loading={isLoading}
                    className="login-button"
                  >
                    Verify Code
                  </Button>

                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={handleResendOtp}
                    disabled={resendTimer > 0}
                    loading={isLoading}
                    c={resendTimer > 0 ? "dimmed" : "blue"}
                  >
                    {resendTimer > 0
                      ? `Resend Code in ${resendTimer}s`
                      : "Resend Code"}
                  </Button>

                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                      setStep("login");
                      setResendTimer(0);
                    }}
                  >
                    Back to Login
                  </Text>
                </Stack>
              </Stack>
            )}
          </Paper>
        </div>
      </Container>
    </div>
  );
};

export default Login;
